import type { PoiDetailProps } from './poi-detail-horizontal';
import { buildPoiDetailTabOrders } from './poi-detail-horizontal';
import clsx from 'clsx';
import styles from './styles.module.scss';
import PlaceHolder from '@/components/icons/shop/PlaceHolder';
import { ModalButton } from '@/components/common/modal-button';
import { DirectionIcon, MarkerIcon } from '@/components/icons';
import { useTranslation } from 'react-i18next';
import { useConfigStore } from '@/stores/configStore';
import type { BuildingInStoreInfo } from '@/services/dto';

const PoiDetailVertical = ({
    poiDetail,
    language,
    buildings,
    onLocationCheck,
    onGuideStart,
}: {
    poiDetail: PoiDetailProps;
    language: string;
    buildings: BuildingInStoreInfo[];
    onLocationCheck: () => void;
    onGuideStart: () => void;
}) => {
    const { t } = useTranslation();
    const contrast = useConfigStore((s) => s.config?.ui?.contrast);
    const showBuildingName = buildings?.length > 1;

    const tabOrders = buildPoiDetailTabOrders(poiDetail);
    const r = tabOrders.regionOrder;

    return (
        <div className={clsx(styles['poi-modal__layout'], styles['poi-modal__layout--vertical'])}>
            <div className={clsx(styles['poi-modal__header-vertical'])}>
                <h1
                    data-interactive
                    data-modal-region
                    tabIndex={tabOrders.name}
                    data-modal-region-order={r.header}
                    aria-label={poiDetail?.poiName}
                    className={clsx(styles['poi-modal__title'], 'focus-border')}
                >
                    {poiDetail?.poiName}
                </h1>
                <p
                    className={clsx(styles['poi-modal__meta'], 'focus-border')}
                    data-interactive
                    tabIndex={tabOrders.meta}
                    aria-label={showBuildingName ? `${poiDetail?.floorName} ${poiDetail?.buildingName}` : poiDetail?.floorName}
                >
                    <span>{poiDetail?.floorName}</span>
                    {showBuildingName && (
                        <>
                            ·<span>{poiDetail?.buildingName}</span>
                        </>
                    )}
                </p>
            </div>

            <div className={clsx(styles['poi-modal__media'], styles['poi-modal__media--vertical'], 'focus-border')}>
                {poiDetail?.imgUrlDetail ? (
                    <img src={poiDetail.imgUrlDetail} alt={poiDetail?.poiName} />
                ) : (
                    <div className={styles['poi-modal__media-empty']}>
                        <PlaceHolder variant="detail" />
                    </div>
                )}
            </div>

            <div className={clsx(styles['poi-modal__panel'], styles['poi-modal__panel--vertical'])}>
                <div className={clsx(styles['poi-modal__content'], styles['poi-modal__content--vertical'], 'scrollbar-custom-gray-99938f scrollbar-w-6')}>
                    <div className={styles['poi-modal__rows']}>
                        {poiDetail?.listContent?.map((content, index) => (
                            <div
                                className={clsx(styles['poi-modal__row'], 'focus-border')}
                                key={content.key}
                                data-interactive
                                {...(index === 0 ? { 'data-modal-region': true, 'data-modal-region-order': r.shopInfo } : {})}
                                tabIndex={tabOrders.shopInfo[index]}
                                aria-label={`${content.name?.[language]} ${content.content?.[language]}`}
                            >
                                <div className={styles['poi-modal__row-key']}>{content.name?.[language]?.slice(0, 12)}</div>
                                <div className={styles['poi-modal__row-name']}>{content.content?.[language]}</div>
                            </div>
                        ))}
                    </div>

                    {poiDetail?.desc && (
                        <div
                            className={clsx(styles['poi-modal__desc'], styles['poi-modal__desc--vertical'], 'focus-border')}
                            data-interactive
                            tabIndex={tabOrders.desc}
                            aria-label={poiDetail?.desc}
                        >
                            {poiDetail?.desc}
                        </div>
                    )}

                    {poiDetail?.keywords.length > 0 && (
                        <div className={clsx(styles['poi-modal__tags'], styles['poi-modal__tags--vertical'])}>
                            {poiDetail?.keywords.map((keyword, index) => (
                                <span
                                    key={keyword}
                                    className={clsx(styles['poi-modal__tag'], 'focus-border', {
                                        [styles['poi-modal__tag--contrast']]: contrast,
                                    })}
                                    data-interactive
                                    tabIndex={tabOrders.keywords[index]}
                                    aria-label={keyword}
                                >
                                    {keyword}
                                </span>
                            ))}
                        </div>
                    )}
                </div>

                <div className={clsx(styles['poi-modal__actions'], styles['poi-modal__actions--vertical'])}>
                    <ModalButton
                        size="fit"
                        variant="secondary"
                        className={clsx(styles['poi-modal__action-button'], styles['poi-modal__action-button--vertical'])}
                        type="button"
                        role="button"
                        tabIndex={tabOrders.location}
                        data-modal-region
                        data-modal-region-order={r.location}
                        data-interactive
                        aria-label={t('shop.modal.text.buttons.location_check')}
                        icon={<MarkerIcon width={28} height={28} />}
                        onClick={onLocationCheck}
                        data-role={t('common.data_role.button')}
                    >
                        {t('shop.modal.text.buttons.location_check')}
                    </ModalButton>

                    <ModalButton
                        size="fit"
                        variant="primary"
                        className={clsx(styles['poi-modal__action-button'], styles['poi-modal__action-button--vertical'])}
                        type="button"
                        role="button"
                        tabIndex={tabOrders.guide}
                        data-modal-region
                        data-modal-region-order={r.guide}
                        data-interactive
                        aria-label={t('shop.modal.text.buttons.direction_guide')}
                        icon={<DirectionIcon width={28} height={28} />}
                        onClick={onGuideStart}
                        data-role={t('common.data_role.button')}
                    >
                        {t('shop.modal.text.buttons.direction_guide')}
                    </ModalButton>
                </div>
            </div>
        </div>
    );
};

export default PoiDetailVertical;
